import {
  Container,
  Grid,
  Table,
  TableHead,
  TableBody,
  TableCell,
  TableRow,
  Button,
  Typography,
  LinearProgress,
  TextField,
  Box,
} from "@mui/material";
import {
  FunctionComponent,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";
import { useParams, useHistory } from "react-router";
import { primaryColor } from "../../assets/colors";
import PostNord from "../../assets/img/postnord_erhverv.jpg";
import TNT from "../../assets/img/TNT.png";
import EasyPartz from "../../assets/img/easypartz.jpg";
import AppStateContext from "../../contexts/AppStateContext";
import { useDelayReasons } from "hooks/useDelayReasons";
import { Add, Edit, Receipt } from "@mui/icons-material";
import {
  apiGetOrderInvoice,
  apiGetOrder,
  apiCreateShipment,
  apiGetOrderPdf,
} from "../../util/network/Orders";
import ManufacturedCartItemView from "../../components/OrderView/ManufacturedCartItemView";
import { Finishes, Materials } from "../../util/MaterialUtils";
import ThreadDisplay from "../../components/OrderView/ThreadDisplay";
import AmountTable from "./orderedit_components/AmountTable";
import {
  OrderItemType,
  OrderStatusEnum,
  OrderType,
} from "types/OrderTypes";
import {
  apiCreateDraftCartFromCart,
  apiGetPaymentTerms,
} from "../../util/network/Carts";
import { PaymentTermType } from "types/CartTypes";
import {
  CNCProductDetailsType,
  ProductTypeEnum,
} from "types/products/ProductCommandsType";
import { reformatDate } from "../../util/formatter";
import styles from "./orderedit_components/Styles/orderView.module.css";
import OtherOrderItemRow from "components/OrderView/OtherOrderItemRow";
import InvoiceModal from "components/Modals/InvoiceModal";
import FeeOrderItemRow from "components/OrderView/FeeOrderItemRow";
import ProjectTable from "pages/project_management/ProjectTable";
import OutsourcingTable from "pages/outsourcing_management/OutsourcingTable";
import InquiryDialog from "pages/outsourcing_management/InquiryDialog";
import OrderPurchasingPanel from "./OrderPurchasingPanel";
import RequisitionCreateModal from "pages/outsourcing_management/requisitions/RequisitionCreateModal";
import { InquiryType } from "types/InquiryTypes";
import { StatusAck } from "hooks/useInquiryStatusUpdates";
import { AltRoute } from "@mui/icons-material";
import Tooltip from "@mui/material/Tooltip";
import { useToast } from "contexts/ToastContext";
import Modal from "components/Modals/Modal";
import { ReactComponent as OrderConfimPdfIcon } from "../../assets/svg/dwnlOrderConfirm.svg";
import ValidityIndicator from "components/ValidityIndicator/ValidityIndicator";

type ParamsType = {
  id: string;
};

const OrderView: FunctionComponent = () => {
  const history = useHistory();
  // The inquiries panel further down: which one is open, and a refresh flag so
  // a status change made in the dialog reloads the list behind it.
  const [viewedInquiry, setViewedInquiry] = useState<InquiryType>();
  const [inquiryRefresh, setInquiryRefresh] = useState(false);
  // Opening an inquiry acknowledges its marker; the list patches that row.
  const [inquiryStatusAck, setInquiryStatusAck] = useState<StatusAck>();
  const { strings, token, setShowLoadingModal } = useContext(AppStateContext);
  const { labelFor: labelForDelayReason } = useDelayReasons();
  const { addToast } = useToast();

  const { id } = useParams<ParamsType>();
  const orderId = parseInt(id);
  const [order, setOrder] = useState<OrderType>();
  const [loading, setLoading] = useState<boolean>(false);

  const [invoiceExist, setInvoiceExist] = useState<boolean>();
  const lastDraft = localStorage.getItem("lastDraft");
  const [reqProjects, setReqProjects] = useState<OrderType>();
  const [paymentTerms, setPaymentTerms] = useState<PaymentTermType[]>([]);

  const downloadOrderPdf = () => {
    apiGetOrderPdf(token, orderId, "confirmation")
      .then((response) => response.blob())
      .then((blob) => {
        const url = window.URL.createObjectURL(
          new Blob([blob], { type: "application/pdf" })
        );
        const link = document.createElement("a");
        link.href = url;
        link.setAttribute("download", orderId.toString());
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
      })
      .catch((e) => window.alert(e));
  };

  const createNewFromThis = useCallback(
    (e: any) => {
      setShowLoadingModal(true);
      if (order) {
        apiCreateDraftCartFromCart(token, order?.quoteId).then((res) => {
          localStorage.removeItem("orderEditing");
          history.push(`/quotes/${res.id}`, {
            author: order.author.id,
          });
        });
      } else {
        setShowLoadingModal(false);
      }
    },
    [history, id, lastDraft, setShowLoadingModal, token, order]
  );

  const editOrder = useCallback(
    (e: any) => {
      setShowLoadingModal(true);
      if (order) {
        apiCreateDraftCartFromCart(token, order?.quoteId).then((res) => {
          history.push(`/quotes/${res.id}`, {
            orderId: order.id,
          });
        });
      } else {
        setShowLoadingModal(false);
      }
    },
    [history, id, lastDraft, setShowLoadingModal, token, order]
  );

  const threads = useMemo(() => {
    if (order) {
      const threadList: any[] = [];
      for (const item of order.items) {
        if (item.product) {
          if (
            item.product.productType === ProductTypeEnum.CNC &&
            item.product.details
          ) {
            const details = item.product
              .details as unknown as CNCProductDetailsType;
            threadList.push(...details.threads);
          }
        }
      }
      return <ThreadDisplay specs={threadList} />;
    }
    return null;
  }, [order]);

  useEffect(() => {
    if (order?.id) {
      if (order.invoiceId) {
        setInvoiceExist(true);
      } else {
        apiGetOrderInvoice(token, order.id)
          .then((res) => {
            setInvoiceExist(res !== null);
          })
          .catch((err) => {
            console.error(err);
          });
      }
    }
  }, [order, token]);

  useEffect(() => {
    Materials.loadMaterials();
    Finishes.loadFinishes();
    apiGetOrder(token, orderId).then((res) => {
      setOrder(res);

      apiGetPaymentTerms(token)
        .then((terms) => setPaymentTerms(terms))
        .catch(() => {});
    });
  }, [id, token]);

  const getShippingText = (id: string) => {
    console.log(id);
    if (id.includes("postnord")) {
      return {
        title: strings.PostNordDelivery,
        description: strings.PostNordDeliveryText,
        timeText: strings.PostNordDeliveryTime,
        logo: PostNord,
      };
    }
    if (id.includes("tnt_before_12")) {
      return {
        title: strings.TNT2Titel,
        description: strings.TNT2Body,
        timeText: strings.TNT2DeliveryTime,
        logo: TNT,
      };
    }
    if (id.includes("tnt_next_day")) {
      return {
        title: strings.TNT3Titel,
        description: strings.TNT3Body,
        timeText: strings.TNT3DeliveryTime,
        logo: TNT,
      };
    }
    return {
      title: strings.SelfCollect,
      description: strings.SelfCollectText,
      timeText: strings.SelfCollectTime,
      logo: EasyPartz,
    };
  };

  const [invoiceModalOpen, setInvoiceModalOpen] = useState<boolean>(false);
  const [shipmentModalOpen, setShipmentModalOpen] = useState<boolean>(false);
  const closeInquiry = () => {
    setReqProjects(undefined);
  };

  const confirmShipmentModal = () => {
    return (
      <Modal
        isOpen={shipmentModalOpen}
        onClose={() => {
          setShipmentModalOpen(false);
        }}
      >
        <div>
          <Typography variant={"h6"} fontWeight={"bold"}>
            Are you sure you want to create a shipment for this order?
          </Typography>
          <Typography variant={"body1"}>
            Shipments should be created automatically, only create a shipment
            manually if you are sure it is needed.
          </Typography>
          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              marginTop: "1rem",
            }}
          >
            <Button
              variant="contained"
              size="small"
              color="secondary"
              onClick={() => setShipmentModalOpen(false)}
            >
              CANCEL
            </Button>
            <Button
              variant="contained"
              size="small"
              onClick={() => {
                apiCreateShipment(token, orderId)
                  .then((res) => {
                    addToast({
                      type: "success",
                      message: "Shipment created",
                    });
                  })
                  .catch((err) => {
                    addToast({
                      type: "error",
                      message: "Error creating shipment",
                    });
                  })
                  .finally(() => {
                    setShipmentModalOpen(false);
                  });
              }}
              color={"primary"}
              style={{
                background: "#E0A900",
                border: "none",
                borderRadius: "0px",
              }}
            >
              CONFIRM
            </Button>
          </div>
        </div>
      </Modal>
    );
  };

  return (
    <Container maxWidth={false} style={{ padding: "0", margin: "0" }}>
      {/* Outsourcing starts with a requisition now. The part is left for
          the modal's picker: this view knows the order, not which line is
          being sent out. */}
      <RequisitionCreateModal
        open={reqProjects !== undefined}
        onClose={closeInquiry}
        prefill={{ salesOrderId: reqProjects?.id }}
        // The inquiry appears once the requisition is submitted, so the table
        // below is refreshed on save rather than on create.
        onSaved={() => setInquiryRefresh(true)}
      />
      {order && (
        <InvoiceModal
          isOpen={invoiceModalOpen}
          onClose={() => setInvoiceModalOpen(false)}
          onSubmit={(created) => {
            setInvoiceExist(created);
          }}
          order={order}
        />
      )}
      {confirmShipmentModal()}
      <div>
        <div className={`${styles.header__container}`}>
          <div>
            <Typography
              variant={"h6"}
              fontWeight={"bold"}
              className={`${styles.header__text}`}
            >
              Order #{order?.orderNo}
            </Typography>
          </div>
          <div className={`${styles.button__container}`}>
            <div className={`${styles.button}`}>
              <Tooltip title="Order Confirmation Email">
                <Button
                  variant="contained"
                  size="small"
                  onClick={downloadOrderPdf}
                  style={{
                    background: "var(--lightMain)",
                    border: "none",
                    borderRadius: "0px",
                  }}
                >
                  <OrderConfimPdfIcon />
                  <span style={{ paddingLeft: "0.5rem" }}>Download</span>
                </Button>
              </Tooltip>
            </div>
            <div className={`${styles.button}`}>
              <Tooltip title="Outsource">
                <Button
                  variant="contained"
                  size="small"
                  onClick={(e: any) => {
                    e.stopPropagation();
                    if (!order) {
                      apiGetOrder(token, orderId).then((res) => {
                        setOrder(res);
                        setReqProjects(res);
                      });
                    } else {
                      setReqProjects(order);
                    }
                  }}
                  style={{
                    background: "var(--buttonYellow)",
                    border: "none",
                    borderRadius: "0px",
                  }}
                >
                  <AltRoute />
                  <span style={{ paddingLeft: "0.5rem" }}>Outsource</span>
                </Button>
              </Tooltip>
            </div>
            <div className={`${styles.button}`}>
              <Button
                variant="contained"
                size="small"
                onClick={() => {
                  setInvoiceModalOpen(true);
                }}
                color={invoiceExist ? "info" : "primary"}
                style={{
                  background: invoiceExist ? "#E0A900" : "#C2C2C2",
                  border: "none",
                  borderRadius: "0px",
                }}
              >
                <Receipt fontSize="medium" />{" "}
                {invoiceExist ? "RESEND INVOICE" : "SEND INVOICE"}
              </Button>
            </div>
            <div className={`${styles.button}`}>
              <Button
                variant="contained"
                size="small"
                onClick={() => {
                  setShipmentModalOpen(true);
                }}
                color={"info"}
                style={{
                  border: "none",
                  borderRadius: "0px",
                }}
              >
                {"CREATE SHIPMENT"}
              </Button>
            </div>

            <div className={`${styles.button}`}>
              <Tooltip title={"Edit this order"}>
                <span>
                  <Button
                    variant="contained"
                    style={{
                      background: "#E0A900",
                      border: "none",
                      borderRadius: "0px",
                    }}
                    size="small"
                    onClick={editOrder}
                    disabled={
                      order?.status === OrderStatusEnum.COMPLETE ||
                      order?.status === OrderStatusEnum.CANCELLED
                    }
                  >
                    <Edit fontSize="small" /> EDIT
                  </Button>
                </span>
              </Tooltip>
            </div>

            <div className={`${styles.button}`}>
              <Button
                variant="contained"
                // color={"primary"}
                size="small"
                onClick={createNewFromThis}
                style={{
                  background: "#E0A900",
                  border: "none",
                  borderRadius: "0px",
                }}
              >
                <Add fontSize="medium" /> CREATE NEW FROM THIS
              </Button>
            </div>
          </div>
        </div>
      </div>
      <div className={`${styles.overview__container}`}>
        <div>
          <div className={`${styles.details__titel}`}>Details</div>
          <div className={`${styles.details__container_content}`}>
            <div style={{ paddingRight: "1.5rem" }}>
              <div className={`${styles.details__column_order}`}>
                <span style={{ fontSize: "0.75rem" }}>Order Date</span>
                <span style={{ wordWrap: "break-word", width: "220px" }}>
                  {order?.created_by}
                </span>
                <span>{reformatDate(order?.created)}</span>
              </div>
              <div className={`${styles.details__column_author}`}>
                <span style={{ fontSize: "0.75rem" }}>Author</span>
                <span
                  style={{
                    color: "#007474",
                    wordWrap: "break-word",
                    width: "220px",
                  }}
                >
                  {order?.author.email}
                </span>
              </div>
              <div className={`${styles.details__column_account}`}>
                <span style={{ fontSize: "0.75rem" }}>Last Modified</span>
                <span>{order?.modified_by}</span>
                <span>{reformatDate(order?.modified)}</span>
              </div>
            </div>
            <div style={{ width: "5vw" }}>
              <div className={`${styles.details__column_account}`}>
                <span style={{ fontSize: "0.75rem" }}>Account</span>
                <span style={{ color: "#007474" }}>
                  {order?.billingAddress.company}
                </span>
              </div>
              <div className={`${styles.details__column_user}`}>
                <span style={{ fontSize: "0.75rem" }}>User</span>
                <span style={{ color: "#007474" }}>
                  {order?.billingAddress.firstName}{" "}
                  {order?.billingAddress.lastName}
                </span>
              </div>
              <div className={`${styles.details__column_leadtime}`}>
                <span style={{ fontSize: "0.75rem" }}>Lead Time</span>
                <span>
                  {order?.leadTimeOption} {order?.leadTimeDays}wd
                </span>
              </div>
            </div>
            <div style={{ paddingRight: "0.5rem", width: "5vw" }}>
              <div className={`${styles.details__column_shipping}`}>
                <span style={{ fontSize: "0.75rem" }}>Est. Shipment Date</span>
                <span>{reformatDate(order?.shipmentDate)}</span>
              </div>
              {order?.delayReason && (
                <div className={`${styles.details__column_shipping}`}>
                  <span style={{ fontSize: "0.75rem" }}>Delay Reason</span>
                  <span>{labelForDelayReason(order.delayReason)}</span>
                </div>
              )}
              <div className={`${styles.details__column_state}`}>
                <span style={{ fontSize: "0.75rem" }}>State</span>
                <span>{order?.status}</span>
              </div>
              <div className={`${styles.details__column_currency}`}>
                <span style={{ fontSize: "0.75rem" }}>Currency</span>
                <span>{order?.billingAddress.countryCode}</span>
              </div>
            </div>
          </div>
          {order?.message && (
            <TextField
              label="Customer message"
              value={order?.message}
              disabled
              fullWidth
              multiline
            />
          )}
        </div>
        <div className={`${styles.shipping__container}`}>
          <div className={`${styles.shipping__title}`}>Shipping</div>
          <div className={`${styles.shipping__container_content}`}>
            <div style={{ paddingRight: "2rem" }}>
              <div className={`${styles.shipping__column_type}`}>
                <span style={{ fontSize: "0.75rem" }}>Type</span>
                <span style={{ color: "#007474", textTransform: "capitalize" }}>
                  {order?.shippingMethod} {order?.weight.toFixed(1)}kg
                </span>
              </div>
              <div className={`${styles.shipping__column_company}`}>
                <span style={{ fontSize: "0.75rem" }}>Company</span>
                <span>{order?.shippingAddress.company}</span>
              </div>
              <div className={`${styles.shipping__column_email}`}>
                <span
                  style={{
                    fontSize: "0.75rem",
                  }}
                >
                  Email
                </span>
                <span style={{ wordWrap: "break-word", width: "230px" }}>
                  {order?.shippingAddress.email}
                </span>
              </div>
              <div className={`${styles.shipping__column_address}`}>
                <span style={{ fontSize: "0.75rem" }}>Address</span>
                <span>{order?.shippingAddress.street}</span>
              </div>
              <div className={`${styles.shipping__column_address}`}>
                <span style={{ fontSize: "0.75rem" }}>Post Code</span>
                <span>{order?.shippingAddress.postalCode}</span>
              </div>
              <div className={`${styles.shipping__column_address}`}>
                <span style={{ fontSize: "0.75rem" }}>Country</span>
                <span>{order?.shippingAddress.countryCode}</span>
              </div>
            </div>
            <div>
              <div className={`${styles.shipping__column_shipment}`}>
                <span
                  style={{
                    fontSize: "0.75rem",
                  }}
                >
                  Shipment
                </span>
                <span>{order?.shipping}</span>
              </div>
              <div className={`${styles.shipping__column_shipment}`}>
                <span
                  style={{
                    fontSize: "0.75rem",
                  }}
                >
                  Att
                </span>
                <span>
                  {order?.shippingAddress.firstName}{" "}
                  {order?.shippingAddress.lastName}
                </span>
              </div>
              <div className={`${styles.shipping__column_shipment}`}>
                <span
                  style={{
                    fontSize: "0.75rem",
                  }}
                >
                  Phone
                </span>
                <span>{order?.shippingAddress.phone}</span>
              </div>
              <div className={`${styles.shipping__column_shipment}`}>
                <span
                  style={{
                    fontSize: "0.75rem",
                  }}
                >
                  Address 2
                </span>
                <span>{/* {order?.shippingAddress.street} */}</span>
              </div>
              <div className={`${styles.shipping__column_shipment}`}>
                <span
                  style={{
                    fontSize: "0.75rem",
                  }}
                >
                  City
                </span>
                <span>{order?.shippingAddress.city}</span>
              </div>
            </div>
          </div>
        </div>
        <div className={`${styles.payment__container}`}>
          <div className={`${styles.payment__title}`}>Payment</div>
          <div className={`${styles.payment__container_content}`}>
            <div style={{ paddingRight: "2rem" }}>
              <div className={`${styles.payment__column_type}`}>
                <span style={{ fontSize: "0.75rem" }}>Company</span>
                <span>{order?.billingAddress.company}</span>
              </div>
              <div className={`${styles.payment__column_type}`}>
                <span style={{ fontSize: "0.75rem" }}>Att</span>
                <span>
                  {order?.billingAddress.firstName}{" "}
                  {order?.billingAddress.lastName}
                </span>
              </div>
              <div className={`${styles.payment__column_type}`}>
                <span style={{ fontSize: "0.75rem" }}>Address</span>
                <span>{order?.billingAddress.street}</span>
              </div>
              <div className={`${styles.payment__column_type}`}>
                <span style={{ fontSize: "0.75rem" }}>Post Code</span>
                <span>{order?.billingAddress.postalCode}</span>
              </div>
              <div className={`${styles.payment__column_type}`}>
                <span style={{ fontSize: "0.75rem" }}>City</span>
                <span>{order?.billingAddress.city}</span>
              </div>
              <div className={`${styles.payment__column_type}`}>
                <span style={{ fontSize: "0.75rem" }}>Country</span>
                <span>{order?.billingAddress.countryCode}</span>
              </div>
              <div className={`${styles.payment__column_type}`}>
                <span style={{ fontSize: "0.75rem" }}>Phone</span>
                <span>+45 {order?.billingAddress.phone}</span>
              </div>
              <div className={`${styles.payment__column_type}`}>
                <span style={{ fontSize: "0.75rem" }}>Invoice Email</span>
                <span style={{ wordWrap: "break-word", width: "200px" }}>
                  {order?.billingAddress.email}
                </span>
              </div>
            </div>
            <div>
              <div className={`${styles.payment__column_type}`}>
                <span style={{ fontSize: "0.75rem" }}>Type</span>
                <span style={{ textTransform: "capitalize" }}>
                  {order?.paymentMethod}
                </span>
              </div>
              <div className={`${styles.payment__column_type}`}>
                <span style={{ fontSize: "0.75rem" }}>Payment Terms</span>
                <span>
                  {order?.paymentTermsNumber != null
                    ? paymentTerms.find(
                        (t) => t.paymentTermsNumber === order.paymentTermsNumber
                      )?.name ?? order.paymentTermsNumber
                    : "—"}
                </span>
              </div>
              <div className={`${styles.payment__column_type}`}>
                <span style={{ fontSize: "0.75rem" }}>Invoice</span>
                <span style={{ color: "#007474" }}>#{order?.invoiceId}0</span>
              </div>
              <div className={`${styles.payment__column_type}`}>
                <span style={{ fontSize: "0.75rem" }}>PO Number</span>
                <span>{order?.poNumber ?? "No PO number"}</span>
              </div>
              {order?.eanNumber && (
                <div className={`${styles.payment__column_type}`}>
                  <span style={{ fontSize: "0.75rem" }}>EAN</span>
                  <Box sx={{ display: "flex", alignItems: "center" }}>
                    <span>{order.eanNumber}</span>
                    <ValidityIndicator
                      valid={order.isEanValidChecksum === false ? false : null}
                      warningTooltip="EAN checksum is invalid — check the number"
                    />
                    <ValidityIndicator
                      valid={order.isEanValidExternal === false ? false : null}
                      warningTooltip="EAN is not registered in Nemhandel"
                    />
                    <ValidityIndicator
                      valid={order.isEanInvoiceDiff === true ? false : null}
                      warningTooltip="EAN differs from the one registered in e-conomic"
                    />
                  </Box>
                </div>
              )}
              {order?.billingAddress.vat && (
                <div className={`${styles.payment__column_type}`}>
                  <span style={{ fontSize: "0.75rem" }}>CVR</span>
                  <Box sx={{ display: "flex", alignItems: "center" }}>
                    <span>{order.billingAddress.vat}</span>
                    <ValidityIndicator
                      valid={order.billingAddress.isCvrValidChecksum === false ? false : null}
                      warningTooltip="CVR checksum is invalid — check the number"
                    />
                    <ValidityIndicator
                      valid={order.billingAddress.isCvrValidExternal === false ? false : null}
                      warningTooltip="CVR could not be verified externally"
                    />
                  </Box>
                </div>
              )}
              <div className={`${styles.payment__column_type}`}>
                <span style={{ fontSize: "0.75rem" }}>
                  Customer reference (Initials)
                </span>
                <span>{order?.initials ?? ""}</span>
              </div>
            </div>
          </div>
        </div>
      </div>
      {/* <Typography
        sx={{
          width: "100%",
          textAlign: "center",
          fontSize: "large",
        }}
      >
        Created: {order?.created}
      </Typography>
      <Typography
        sx={{
          width: "100%",
          textAlign: "center",
          fontSize: "large",
        }}
      >
        Status: {order?.status}
      </Typography>
      <Typography
        sx={{
          width: "100%",
          textAlign: "center",
          fontSize: "large",
        }}
      >
        Lead time: {order?.leadTimeOption} {order?.leadTimeDays} days
      </Typography> */}
      <Grid container direction="row" justifyContent="start">
        {/* <Grid xs={6} md={6}>
          <AccountInfoTable customer={order?.author} disabled />
        </Grid> */}
        <Grid md={6} justifyContent="flex-end" direction="row-reverse"></Grid>
        <Grid xs={12} md={12}>
          {loading ? <LinearProgress /> : <hr color={primaryColor[0]} />}
        </Grid>
        <Grid xs={10} md={10}></Grid>

        <Grid
          xs={12}
          md={12}
          style={{ paddingLeft: "2rem", paddingRight: "2rem" }}
        >
          <Table size="small" aria-label="a dense table">
            <TableHead sx={{ color: "info" }}>
              <TableRow>
                <TableCell colSpan={8} className={`${styles.table__header}`}>
                  <span style={{ color: "#fff" }}>Manufactured</span>
                </TableCell>
              </TableRow>
              <TableRow>
                <TableCell align="left">Status</TableCell>
                <TableCell align="left">Product</TableCell>
                <TableCell align="left">Surface</TableCell>
                <TableCell align="left">Material</TableCell>
                <TableCell align="left">Treatment</TableCell>
                {/* EE-245: left, not center — the cell below holds a flex row
                    with no justifyContent and padding: 0, so its icons pack to
                    the left edge and a centred label floats away from them. */}
                <TableCell align="left">Actions</TableCell>
                <TableCell align="center">Amount</TableCell>
                <TableCell align="right">Price</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {order?.items
                .filter(
                  (i) =>
                    i.product.productType === ProductTypeEnum.CNC ||
                    i.product.productType === ProductTypeEnum.PRINT3D ||
                    i.product.productType === ProductTypeEnum.SHEET
                )
                .map((item: OrderItemType, index: number) => {
                  const data = {
                    ...item.product.details,
                    finish: (item.product.details as any).finish.split(","),
                  };
                  return (
                    <ManufacturedCartItemView
                      key={index}
                      item={item}
                      loading={false}
                      setLoading={setLoading}
                    />
                  );
                })}
            </TableBody>
            <TableHead>
              <TableRow>
                <TableCell colSpan={8} className={`${styles.table__header}`}>
                  <span style={{ color: "#fff" }}>Other</span>
                </TableCell>
              </TableRow>
              <TableRow>
                <TableCell colSpan={2}>Name</TableCell>
                <TableCell colSpan={4}>Description</TableCell>
                <TableCell colSpan={1} align="center">
                  Files
                </TableCell>
                <TableCell colSpan={1} align="right">
                  Price
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {order?.items
                .filter((i) => i.product.productType == ProductTypeEnum.OTHER)
                .map((item: OrderItemType, index: number) => {
                  return <OtherOrderItemRow key={index} item={item} />;
                })}
            </TableBody>
            <TableHead>
              <TableRow>
                <TableCell colSpan={8} className={`${styles.table__header}`}>
                  <span style={{ color: "#fff" }}>Fees</span>
                </TableCell>
              </TableRow>
              <TableRow>
                <TableCell colSpan={2}>Name</TableCell>
                <TableCell colSpan={5}>Description</TableCell>
                <TableCell colSpan={1} align="right">
                  Price
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {order?.items
                .filter((i) => i.product.productType == ProductTypeEnum.FEE)
                .map((item: OrderItemType, index: number) => {
                  return <FeeOrderItemRow key={index} item={item} />;
                })}
            </TableBody>
          </Table>
        </Grid>
        <Grid
          xs={4}
          md={6}
          sx={{ borderRight: 0, borderRadius: "18px", marginRight: "1em" }}
        ></Grid>
        <Grid item xs md style={{ paddingRight: "2rem" }}>
          <AmountTable
            discount={order?.discount}
            shipping={order?.shipping ?? 0}
            total={order?.total ?? 0}
            totalTax={order?.totalTax ?? 0}
            orderWeight={order?.weight ?? 0}
            fees={order?.fees}
          />
        </Grid>
      </Grid>
      <Grid container direction="row" justifyContent="start">
        <Grid
          item
          xs={12}
          md={12}
          style={{ paddingLeft: "2rem", paddingRight: "2rem" }}
        >
          <Typography variant={"h6"} fontWeight={"bold"}>
            Productions
          </Typography>
          <ProjectTable
            selectedMachines={[]}
            selectedUsers={[]}
            section={{
              all: true,
              notStart: false,
              review: false,
              cam: false,
              production: false,
              postprocess: false,
              shipping: false,
            }}
            orderId={orderId}
          />
        </Grid>
      </Grid>
      <Grid container direction="row" justifyContent="start">
        <Grid
          item
          xs={12}
          md={12}
          style={{ paddingLeft: "2rem", paddingRight: "2rem" }}
        >
          {/* EE-253 P3: the requisition lines behind the order and what
              buying them costs this order. */}
          <Typography variant={"h6"} fontWeight={"bold"}>
            Purchasing
          </Typography>
          <Box sx={{ mb: 3 }}>
            <OrderPurchasingPanel orderId={orderId} />
          </Box>
          <Typography variant={"h6"} fontWeight={"bold"}>
            Inquiries
          </Typography>
          <OutsourcingTable
            refresh={inquiryRefresh}
            setRefresh={setInquiryRefresh}
            openInquiry={setViewedInquiry}
            searchValue={""}
            statusFilter={[]}
            orderId={orderId}
            statusAck={inquiryStatusAck}
          />
          {/* Read-only (no `editable`): this page shows the inquiries raised
              against the order. Changing one belongs on the sourcing board. */}
          <InquiryDialog
            open={viewedInquiry !== undefined}
            inquiry={viewedInquiry}
            onClose={() => setViewedInquiry(undefined)}
            // Refreshes the list behind, but leaves the dialog open - the
            // same as the sourcing board does.
            onChanged={() => setInquiryRefresh(true)}
            onStatusAck={setInquiryStatusAck}
          />
        </Grid>
      </Grid>
    </Container>
  );
};

export default OrderView;
